import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Chip, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import {
  WhatsApp, Share, ArrowForward, ContentCopy, Check,
  Lightbulb, CropPortrait, CropLandscape,
} from "@mui/icons-material";

const DOMAIN   = "https://drpawleb.com";
const DISPLAY  = '"Cormorant Garamond", Georgia, serif';
const BLUE     = "#1565C0";
const BABY     = "#42A5F5";

// Category styles are for the dark ShareCard (designed to be screenshotted)
const CATEGORY_STYLES: Record<string, { bg: string; color: string; border: string; glow: string }> = {
  "Nutrition":    { bg: "rgba(34,197,94,.14)",   color: "#86EFAC", border: "rgba(34,197,94,.35)",   glow: "rgba(34,197,94,.15)" },
  "Surgery":      { bg: "rgba(66,165,245,.14)",   color: "#90CAF9", border: "rgba(66,165,245,.35)",  glow: "rgba(66,165,245,.15)" },
  "Emergency":    { bg: "rgba(239,68,68,.14)",    color: "#FCA5A5", border: "rgba(239,68,68,.35)",   glow: "rgba(239,68,68,.15)" },
  "Behavior":     { bg: "rgba(167,139,250,.14)",  color: "#C4B5FD", border: "rgba(167,139,250,.35)", glow: "rgba(167,139,250,.15)" },
  "Pharmacology": { bg: "rgba(245,158,11,.14)",   color: "#FCD34D", border: "rgba(245,158,11,.35)",  glow: "rgba(245,158,11,.15)" },
  "Diagnosis":    { bg: "rgba(20,184,166,.14)",   color: "#5EEAD4", border: "rgba(20,184,166,.35)",  glow: "rgba(20,184,166,.15)" },
  "Wellness":     { bg: "rgba(236,72,153,.14)",   color: "#F9A8D4", border: "rgba(236,72,153,.35)",  glow: "rgba(236,72,153,.15)" },
};
const DEFAULT_CAT = { bg: "rgba(66,165,245,.14)", color: "#90CAF9", border: "rgba(66,165,245,.35)", glow: "rgba(66,165,245,.12)" };

const DrPawLogo: React.FC<{ small?: boolean }> = ({ small }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.2}>
    <Box sx={{
      width: small ? 30 : 42, height: small ? 30 : 42,
      borderRadius: small ? "8px" : "12px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 14px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: small ? ".8rem" : "1.05rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: small ? "1.2rem" : "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>Dr.Paw</Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
const PublicTip: React.FC = () => {
  const [searchParams] = useSearchParams();
  const title    = decodeURIComponent(searchParams.get("title")    || "");
  const body     = decodeURIComponent(searchParams.get("body")     || "");
  const category = decodeURIComponent(searchParams.get("category") || "");
  const stat     = decodeURIComponent(searchParams.get("stat")     || "");
  const emoji    = decodeURIComponent(searchParams.get("emoji")    || "💡");
  const link     = searchParams.get("link") || `${DOMAIN}/#pricing`;

  const [copied,    setCopied]    = useState(false);
  const [storyMode, setStoryMode] = useState(false);

  const cat = CATEGORY_STYLES[category] ?? DEFAULT_CAT;

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    const data = {
      title: `Dr.Paw Tip — ${title}`,
      text: `${title}${body ? `\n\n${body}` : ""}\n\n#DrPaw`,
      url: window.location.href,
    };
    if (typeof navigator.share === "function") {
      try { await navigator.share(data); return; } catch {}
    }
    handleCopy();
  };

  const waMsg = encodeURIComponent(
    `Check this out! 🐾 A vet tip from Dr.Paw:\n\n"${title}"${body ? `\n\n${body}` : ""}\n\nSee more: ${window.location.href}`
  );
  const waUrl = `https://wa.me/?text=${waMsg}`;

  if (!title) {
    return (
      <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#90A4AE" }}>Invalid or missing tip link.</Typography>
      </Box>
    );
  }

  // ── The ShareCard stays dark — it's designed to be screenshotted for social media ──
  const ShareCard = () => (
    <Box sx={{
      background: `linear-gradient(160deg, #060D1A 0%, #0A1628 50%, #0D2137 100%)`,
      border: `1.5px solid ${cat.border}`,
      borderRadius: storyMode ? "24px" : "28px",
      p: storyMode ? { xs: 3, md: 3.5 } : { xs: 3, md: 4 },
      position: "relative", overflow: "hidden",
      textAlign: "center",
      minHeight: storyMode ? 500 : "auto",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      ...(storyMode ? { aspectRatio: "9/16", maxWidth: 360, mx: "auto" } : {}),
      boxShadow: `0 0 60px ${cat.glow}`,
      width: "100%",
    }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg,transparent,${cat.color},transparent)` }} />
      <Box sx={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle,${cat.glow} 0%,transparent 70%)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -40, left: -40, width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.12) 0%,transparent 70%)", pointerEvents: "none" }} />

      <Stack direction="row" justifyContent="center" spacing={1} sx={{ mb: storyMode ? 3 : 2 }}>
        {category && (
          <Chip label={category} size="small" sx={{ bgcolor: cat.bg, color: cat.color, fontWeight: 700, fontSize: ".72rem", border: `1px solid ${cat.border}` }} />
        )}
        <Chip label="Dr.Paw" size="small" sx={{ bgcolor: "rgba(21,101,192,.2)", color: "#90CAF9", fontWeight: 700, fontSize: ".72rem" }} />
      </Stack>

      <Typography sx={{ fontSize: storyMode ? "4rem" : { xs: "3rem", md: "4rem" }, lineHeight: 1, mb: storyMode ? 1.5 : 1.5 }}>
        {emoji}
      </Typography>

      {stat && (
        <Box sx={{ width: "100%", overflow: "hidden", mb: 1, px: 0.5 }}>
          <Typography sx={{
            color: cat.color, fontWeight: 700,
            fontSize: storyMode ? { xs: "2.8rem", md: "3.2rem" } : { xs: "3rem", md: "4rem" },
            lineHeight: 1, letterSpacing: "-.02em",
            wordBreak: "break-word", overflowWrap: "anywhere", width: "100%",
            fontFamily: DISPLAY,
          }}>
            {stat}
          </Typography>
        </Box>
      )}

      <Typography sx={{
        color: "#fff", fontWeight: 700,
        fontSize: storyMode ? { xs: "1.15rem", md: "1.4rem" } : { xs: "1.3rem", md: stat ? "1.6rem" : "2rem" },
        lineHeight: 1.25, letterSpacing: "-.01em",
        mb: body ? 1.5 : (storyMode ? 2.5 : 2),
        wordBreak: "break-word", width: "100%", px: 0.5,
        fontFamily: DISPLAY,
      }}>
        {title}
      </Typography>

      {body && (
        <Typography sx={{
          color: "rgba(255,255,255,.52)",
          fontSize: storyMode ? ".82rem" : ".9rem",
          lineHeight: 1.75,
          mb: storyMode ? 2 : 2,
          wordBreak: "break-word",
          width: "100%",
          px: 0.5,
        }}>
          {body}
        </Typography>
      )}

      <Typography sx={{ color: "rgba(255,255,255,.18)", fontSize: ".68rem", letterSpacing: ".12em", textTransform: "uppercase" }}>
        drpawleb.com • #DrPaw
      </Typography>

      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg,transparent,${cat.color}55,transparent)` }} />
    </Box>
  );

  const metaTitle = title ? `${title} | Dr.Paw Pet Care Tip` : "Pet Care Tip — Dr.Paw";
  const metaDesc  = body
    ? `${body.slice(0, 120)} — Dr.Paw pet-care courses.`
    : title
    ? `${title} — Learn more with Dr.Paw, vet-written pet-care courses for pet parents.`
    : "Vet-written pet care tips from Dr.Paw. Learn how to keep your pet healthy and happy.";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", top: -100, right: -100, width: 450, height: 450, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 2.5, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontWeight: 600, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Daily Vet Insight
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 580 }}>

          {/* Story / Post toggle */}
          <Stack direction="row" justifyContent="center" sx={{ mb: 2.5 }}>
            <ToggleButtonGroup
              value={storyMode ? "story" : "post"}
              exclusive
              onChange={(_, v) => { if (v) setStoryMode(v === "story"); }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": { borderColor: "rgba(21,101,192,.15)", color: "#546E7A", textTransform: "none", px: 2.5, fontSize: ".8rem", fontWeight: 600 },
                "& .Mui-selected": { bgcolor: `rgba(21,101,192,.1) !important`, color: `${BLUE} !important`, borderColor: `rgba(21,101,192,.3) !important` },
              }}
            >
              <ToggleButton value="post"><CropLandscape sx={{ fontSize: "1rem", mr: 0.8 }} />Post Card</ToggleButton>
              <ToggleButton value="story"><CropPortrait sx={{ fontSize: "1rem", mr: 0.8 }} />Story Mode</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* The dark card (for sharing) */}
          <Box sx={{ mb: 2 }}>
            <ShareCard />
          </Box>

          {/* Screenshot hints */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Box sx={{ background: "rgba(21,101,192,.04)", border: "1px dashed rgba(21,101,192,.15)", borderRadius: "10px", px: 2, py: 1.2, textAlign: "center" }}>
              <Typography sx={{ color: "#546E7A", fontSize: ".78rem" }}>
                📸 Screenshot {storyMode ? "— perfect for Instagram & WhatsApp Stories" : "— great for feed posts & carousels"}
              </Typography>
            </Box>
            <Box sx={{ background: "rgba(66,165,245,.05)", border: "1px solid rgba(66,165,245,.2)", borderRadius: "10px", px: 2, py: 1.1, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
              <Typography sx={{ fontSize: ".78rem" }}>📱</Typography>
              <Typography sx={{ color: "#546E7A", fontSize: ".78rem" }}>
                Tag <strong style={{ color: BLUE }}>#DrPaw</strong> and <strong style={{ color: BLUE }}>#DrPawTips</strong> — we reshare every one!
              </Typography>
            </Box>
          </Stack>

          {/* Share buttons */}
          <Stack spacing={1.5} sx={{ mb: 2.5 }}>
            <Button
              variant="contained" startIcon={<Share />} onClick={handleShare} fullWidth
              sx={{ py: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 700, fontSize: ".95rem", background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 16px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
            >
              Share to Story
            </Button>
            <Button
              variant="outlined" startIcon={<WhatsApp />} href={waUrl} target="_blank" fullWidth
              sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" } }}
            >
              Share on WhatsApp
            </Button>
          </Stack>

          {/* CTA */}
          <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.15)", borderRadius: "20px", boxShadow: "0 4px 16px rgba(21,101,192,.06)", p: { xs: 2.5, md: 3 }, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "11px", background: "linear-gradient(135deg,#1565C0,#42A5F5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Lightbulb sx={{ color: "#fff", fontSize: "1.1rem" }} />
              </Box>
              <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".95rem" }}>Want to go deeper?</Typography>
            </Stack>
            <Typography sx={{ color: "#546E7A", fontSize: ".85rem", lineHeight: 1.75, mb: 2 }}>
              Dr.Paw has expert-led courses covering exactly these topics — built by real vets, for real vets.
            </Typography>
            <Button
              variant="contained" href={link} endIcon={<ArrowForward />} fullWidth
              sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg,#1565C0,#42A5F5)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-2px)" }, transition: "all .2s ease" }}
            >
              Explore Dr.Paw Courses
            </Button>
          </Box>

          {/* Copy link */}
          <Button
            variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
            sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", "&:hover": { borderColor: copied ? "rgba(34,197,94,.5)" : BLUE, color: copied ? "#22C55E" : BLUE }, transition: "all .2s ease" }}
          >
            {copied ? "Link Copied!" : "Copy Tip Link"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicTip;
